import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import Input from "../../components/form/input";
import Btn from "../../components/form/btn";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export default function CompleteProfilePhone() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const { user, updateUser } = useAuth();
  
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    updatePageTitle(t("auth.completeProfile") + " - " + t("auth.phone"), t("auth.enterPhoneNumber"));
    
    if (!user) {
      navigate("/signin");
      return;
    }

    // If user doesn't have account type, redirect to first step
    if (!user.accountType || user.accountType.trim() === '') {
      navigate("/complete-profile");
      return;
    }

    // If user already has phone, redirect to next step
    if (user.phone && user.phone.trim() !== '') {
      // Determine next step based on account type
      if (user.accountType === "organization") {
        navigate("/complete-profile/documents");
      } else {
        navigate("/complete-profile/gender");
      }
      return;
    }

    // Pre-fill existing phone if available
    setPhone(user.phone || "");
  }, [user, navigate, updatePageTitle, t]);

  // Phone Functions
  const validatePhone = (phone) => {
    // Simple validation - just check if it has reasonable length
    return phone.length >= 8 && phone.length <= 20;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: "" }));
    }
  };

  const handlePhoneSubmit = async () => {
    const newErrors = {};

    if (!phone.trim()) {
      newErrors.phone = t("errors.phoneRequired");
    } else if (!validatePhone(phone)) {
      newErrors.phone = t("errors.phoneLength");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/user/set-phone`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ phone: phone.trim() })
      });

      if (!response.ok) {
        throw new Error(t("errors.failedToUpdatePhone"));
      }

      // Update user data
      const updatedUser = { ...user, phone: phone.trim() };
      updateUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Navigate to next step based on account type
      if (user.accountType === "organization") {
        // Organizations automatically get gender set as "other" and skip to documents
        try {
          const genderResponse = await fetch(`${API_URL}/user/set-gender`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ gender: "other" })
          });
          
          if (genderResponse.ok) {
            const finalUpdatedUser = { ...updatedUser, gender: "other" };
            updateUser(finalUpdatedUser);
            localStorage.setItem("user", JSON.stringify(finalUpdatedUser));
            navigate("/complete-profile/documents");
            return;
          }
        } catch (genderError) {
          console.error("Failed to set gender for organization:", genderError);
        }
        
        navigate("/complete-profile/documents");
      } else {
        navigate("/complete-profile/gender");
      }

    } catch (error) {
      setErrors({ phone: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Indicator */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg">
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">✓</div>
              <span className="text-gray-600 font-medium">{t("auth.accountType")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-[#F15213] rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
              <span className="text-gray-600 font-medium">{t("auth.phone")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center space-x-2 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">3</div>
              <span className="text-gray-400 font-medium">{t("auth.details")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center space-x-2 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">4</div>
              <span className="text-gray-400 font-medium">{t("auth.documents")}</span>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#F15213] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="w-10 h-10 text-white">
                <path fill="currentColor" d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3l49.4-40.4c13.7-11.1 18.4-30 11.6-46.3l-40-96z"/>
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#F15213] mb-4">
              {t("auth.phoneNumber")}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t("auth.phoneNumberDescription")}
            </p>
          </div>

          {errors.phone && (
            <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.phone}</p>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-md mx-auto mb-12">
            <div className="relative">
              <Input
                type="tel"
                placeholder={t("auth.enterPhoneNumber")}
                value={phone}
                onChange={handlePhoneChange}
                id="phone"
                label={t("auth.phoneNumber")}
                error={errors.phone}
                className="text-lg py-4 pl-12 bg-gray-50 border-2 border-gray-200 focus:border-[#F15213] focus:bg-white transition-all duration-300 rounded-2xl"
              />
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={() => navigate("/complete-profile")}
              className="w-full sm:w-auto px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-2xl hover:bg-gray-200 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {t("common.back")}
            </button>
            
            <Btn
              onClick={handlePhoneSubmit}
              disabled={isLoading || !phone.trim()}
              className="w-full sm:w-auto px-12 py-4 bg-[#F15213] text-white font-bold rounded-2xl hover:bg-[#E04A11] transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? t("common.processing") : t("common.next")}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
} 